import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

import { LoadScreen } from "@/core/components/shared";
import { Alert, AlertDescription } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import type { KycDocumentDto } from "@/features/kyc/api/dto";

type KycPreviewKind = "image" | "pdf" | "unknown";

const IMAGE_MIMES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

/**
 * Détermine si une pièce KYC est affichable en image, en PDF, ou seulement
 * via un repli (nouvel onglet). `mimeType` prime : les clés de stockage
 * Postman n'ont souvent pas d'extension.
 */
function resolveKycPreviewKind({
  mimeType,
  storageKey,
  downloadUrl,
}: {
  mimeType?: string | null;
  storageKey?: string | null;
  downloadUrl?: string | null;
}): KycPreviewKind {
  const mime = mimeType?.toLowerCase().trim();
  if (mime && IMAGE_MIMES.has(mime)) return "image";
  if (mime === "application/pdf") return "pdf";

  for (const candidate of [storageKey, downloadUrl]) {
    const path = (candidate ?? "").toLowerCase().split("?")[0] ?? "";
    if (/\.(jpe?g|png|webp)$/.test(path)) return "image";
    if (/\.pdf$/.test(path)) return "pdf";
  }
  return "unknown";
}

/**
 * Lightbox de consultation d'une pièce KYC à partir de l'URL présignée GET
 * (`download-url`). Hors scope de la règle preview-mutation : GET de
 * consultation, pas d'écriture métier.
 */
export function KycDocumentPreviewDialog({
  open,
  onOpenChange,
  document,
  downloadUrl,
  loading,
  onRetry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document?: KycDocumentDto;
  downloadUrl?: string | null;
  loading: boolean;
  onRetry?: () => void;
}) {
  const { t } = useTypedTranslation();
  const tDoc = (key: string) => t(key as TFunctionType);
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    setMediaError(false);
  }, [downloadUrl]);

  const kind = resolveKycPreviewKind({
    mimeType: document?.mimeType,
    storageKey: document?.storageKey,
    downloadUrl,
  });
  const ready = Boolean(downloadUrl) && !loading;
  const showImage =
    ready && !mediaError && (kind === "image" || kind === "unknown");
  const showPdf =
    ready && (kind === "pdf" || (kind === "unknown" && mediaError));
  const showImageFailedFallback = ready && kind === "image" && mediaError;

  const openInNewTab = () => {
    if (!downloadUrl) return;
    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] sm:max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="pr-8">
            {tDoc("kyc.documents.preview.title")}
            {document?.documentType ? (
              <>
                {" — "}
                <NoTranslate as="span">{document.documentType}</NoTranslate>
              </>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {document?.mimeType ? (
              <NoTranslate as="span">{document.mimeType}</NoTranslate>
            ) : (
              tDoc("kyc.documents.preview.description")
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="relative min-h-[12rem] flex-1 overflow-auto rounded-md bg-muted">
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <LoadScreen />
            </div>
          ) : null}

          {!loading && !downloadUrl ? (
            <div className="flex h-48 flex-col items-center justify-center gap-3 p-4">
              <p className="text-center text-sm text-muted-foreground">
                {tDoc("kyc.documents.preview.loadError")}
              </p>
              {onRetry ? (
                <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                  {t("common.retry")}
                </Button>
              ) : null}
            </div>
          ) : null}

          {showImage ? (
            <img
              src={downloadUrl ?? undefined}
              alt={tDoc("kyc.documents.preview.imageAlt")}
              className="mx-auto max-h-[70vh] w-auto max-w-full object-contain"
              onError={() => setMediaError(true)}
            />
          ) : null}

          {showPdf ? (
            <iframe
              src={downloadUrl ?? undefined}
              title={tDoc("kyc.documents.preview.title")}
              className="h-[70vh] w-full border-0 bg-background"
            />
          ) : null}

          {showImageFailedFallback ? (
            <div className="p-4">
              <Alert>
                <AlertDescription>
                  {tDoc("kyc.documents.preview.loadError")}
                </AlertDescription>
              </Alert>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={!downloadUrl}
            onClick={openInNewTab}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            {tDoc("kyc.documents.preview.openInNewTab")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
